import {
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createContext } from 'react';
import {
  useShowCourse,
  useUpdateCourseProgress,
} from '@/data/corporate-university/course';
import type { Course, Lesson } from '@/data/corporate-university/interfaces';
import {
  useShowLessonVideo,
  useUpdateLessonProgress,
} from '@/data/corporate-university/lessons';
import type {
  LessonsContextValue,
  UpdateLessonProgressParams,
} from '@/packages/corporate-university/context/types';

const LessonsContext = createContext<LessonsContextValue | null>(null);

const getCurrentLesson = (course: Course | null) => {
  const courseProgress = course?.courses_progresses?.[0]?.current_lesson;
  if (courseProgress) {
    return (
      course?.lessons?.find((lesson) => lesson.id === courseProgress) ?? null
    );
  }
  return null;
};

const checkCourseCompletion = (course?: Course | null) => {
  const isCourseCompleted = course?.courses_progresses?.[0]?.is_completed;
  if (isCourseCompleted) {
    return { isCourseCompleted: true, courseProgress: 100 };
  }

  const lessons = course?.lessons;
  if (!lessons || lessons.length === 0) {
    return { isCourseCompleted: false, courseProgress: 0 };
  }

  const completedLessons = lessons.filter(
    (lesson) => lesson?.lessons_progresses?.[0]?.is_completed
  ).length;

  const courseProgress = Math.ceil((completedLessons / lessons.length) * 100);

  return { isCourseCompleted: false, courseProgress };
};

export const LessonsProvider = ({
  children,
  courseId,
}: {
  children: ReactNode;
  courseId: string;
}) => {
  const {
    data: course,
    isLoading: isLoadingCourse,
    error: errorCourse,
  } = useShowCourse(courseId);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);

  useEffect(() => {
    setCurrentLesson(getCurrentLesson(course ?? null));
  }, [course]);

  const { data: lessonVideo, isPending: isPendingLessonVideo } =
    useShowLessonVideo(currentLesson?.id, currentLesson?.type === 'video');

  const { mutate: updateLessonProgress } = useUpdateLessonProgress();

  const { isCourseCompleted, courseProgress } = useMemo(() => {
    return checkCourseCompletion(course);
  }, [course]);

  const { mutate: updateCourseProgress } = useUpdateCourseProgress(courseId);

  const handleUpdateLessonProgress = ({
    lessonId,
    progress,
  }: UpdateLessonProgressParams) => {
    updateLessonProgress(
      { lessonId, progress },
      {
        onSuccess: () => {
          if (progress >= 95) {
            updateCourseProgress({ progress: courseProgress });
          }
        },
      }
    );
  };

  const value = {
    course: course ?? null,
    currentLesson,
    isLoadingCourse,
    errorCourse,
    lessonVideo: lessonVideo?.url ?? null,
    isPendingLessonVideo,
    handleUpdateLessonProgress,
    setCurrentLesson,
    isCourseCompleted,
  };

  return (
    <LessonsContext.Provider value={value}>{children}</LessonsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLessons = () => {
  const context = useContext(LessonsContext);
  if (!context)
    throw new Error('useLessons must be used within LessonsProvider');
  return context;
};
